/**
 * A text layer, drawn as glyph outlines.
 *
 * Not Konva.Text, and not the browser's own text layout. Canvas cannot lay out
 * Arabic - letters join, change shape by position, and mix with left-to-right
 * text in one line - so the shaper does the layout and this draws the result.
 *
 * The pay-off is that what is on screen and what goes to press are the same
 * geometry, not two engines that agree most of the time.
 *
 * Outlines are y-up and canvas is y-down, so every glyph is drawn with
 * scale(s, -s) about its own origin. Flipping the whole line instead would
 * mirror the text.
 */

import { useEffect, useRef, useState } from 'react';
import { Rect, Shape } from 'react-konva';
import type Konva from 'konva';
import type { TextLayer } from '../model/types';
import { isGradient } from '../model/types';
import { mmToPx, toCss } from '../model/units';
import { placeText } from '../text/place';
import { plan } from '../effects/plan';
import { paintPasses, pathFor } from './paint';
import { combine, fillGradientBox, sourceRect } from './mask';
import { imageFor, onAssetsChanged } from '../assets';
import { firstColour } from '../press/draw';
import { onFontsChanged } from '../text/fonts';
import { caretRect, forgetLayouts, layoutFor, selectionRects } from '../text/layout';
import { useEditor } from '../state/store';

interface Props {
  layer: TextLayer;
  zoom: number;
}

/** How fast the caret blinks. Slow enough not to nag, fast enough to find. */
const BLINK_MS = 560;

export function TextNode({ layer, zoom }: Props) {
  const node = useRef<Konva.Shape>(null);
  const [, redraw] = useState(0);

  const editing = useEditor((s) => s.editing === layer.id);
  const caret = useEditor((s) => s.caret);

  // A face arriving asynchronously has to reach the canvas somehow, and the
  // store is the wrong place for it - it is not part of the document.
  useEffect(
    () =>
      onFontsChanged(() => {
        forgetLayouts();
        redraw((n) => n + 1);
      }),
    []
  );

  // A picture inside the letters arrives asynchronously too.
  useEffect(() => onAssetsChanged(() => redraw((n) => n + 1)), []);

  const laid = layoutFor(layer);

  useEffect(() => {
    node.current?.getLayer()?.batchDraw();
  }, [laid, zoom, layer.fill, layer.picture, layer.stroke, layer.effect, layer.curve, layer.path, editing]);

  if (!laid) return null;

  const { paragraph, unitsPerMm } = laid;

  /*
   * The same placement and the same passes the press walk draws (draw.ts), so
   * a curve, a glow or a glitch is one piece of geometry on screen and on the
   * card. While typing, the text is laid straight: the caret and the
   * selection are measured on straight lines, and a caret wandering round an
   * arc a letter behind the text would be worse than a moment of straightness.
   */
  const placed = placeText(layer, laid, editing);
  const picture = layer.picture?.asset ? layer.picture : null;
  const image = picture ? imageFor(picture.asset) : null;
  const passes = plan(
    layer,
    {
      fill: picture ? firstColour(layer.fill) : layer.fill,
      stroke: layer.stroke ? layer.stroke.colour : null,
      width: layer.stroke ? layer.stroke.width : 0,
    },
    placed.extent
  );
  const outlines = placed.glyphs.map((g) => ({ path: pathFor(g.d), m: g.m }));

  // The letters and their rules as one path, for a picture or a ramp inside them.
  const letters = (dx: number, dy: number, rules: boolean) => {
    const all = outlines.slice();

    if (rules) {
      for (const rule of placed.rules) {
        const path = new Path2D();

        path.rect(rule.x, rule.y, rule.w, rule.h);
        all.push({ path, m: [1, 0, 0, 1, 0, 0] });
      }
    }

    return combine(all, [1, 0, 0, 1, dx, dy]);
  };
  const box = { x: 0, y: 0, w: layer.w, h: layer.h };
  const pxPerMm = mmToPx(1, zoom);

  const width = mmToPx(layer.w > 0 ? layer.w : paragraph.width / unitsPerMm, zoom);
  const height = mmToPx(layer.h > 0 ? layer.h : paragraph.height / unitsPerMm, zoom);

  return (
    <>
      {/* The highlight goes behind the letters, as a highlight does. */}
      {editing &&
        selectionRects(layer, caret.start, caret.end).map((box, index) => (
          <Rect
            key={index}
            x={mmToPx(box.x, zoom)}
            y={mmToPx(box.y, zoom)}
            width={mmToPx(box.w, zoom)}
            height={mmToPx(box.h, zoom)}
            fill="rgba(11, 132, 255, 0.28)"
            listening={false}
          />
        ))}

      <Shape
        ref={node}
        // Drawn at the origin. Position, rotation and opacity belong to the group
        // this sits in, so that dragging is one piece of code for every kind of
        // layer rather than eight.
        sceneFunc={(context, shape) => {
          const canvas = context._context;

          canvas.save();
          // Millimetres from here on, as the placement and the passes are.
          canvas.scale(pxPerMm, pxPerMm);

          for (const pass of passes) {
            if (pass.body && picture && pass.fill) {
              // A picture seen through the letters. Until it loads, the stand-in colour.
              if (image) {
                canvas.save();
                canvas.clip(letters(0, 0, true));

                const src = sourceRect(picture, box, { w: image.naturalWidth, h: image.naturalHeight });

                canvas.drawImage(image, src.x, src.y, src.w, src.h, 0, 0, box.w, box.h);
                canvas.restore();
              } else {
                paintPasses(canvas, [{ ...pass, stroke: null }], outlines, { letters: true });
              }

              if (pass.stroke) paintPasses(canvas, [{ ...pass, fill: null }], outlines, { letters: true });
              continue;
            }

            if (isGradient(pass.fill) && pass.source === 'outline' && pass.blur <= 0) {
              // One ramp across the whole text, as the press file draws it.
              for (let n = 0; n < pass.repeat; n++) {
                canvas.save();
                canvas.globalAlpha *= pass.alpha;
                canvas.clip(letters(pass.dx, pass.dy, !!pass.body));
                fillGradientBox(canvas, pass.fill, { ...box, x: pass.dx, y: pass.dy });
                canvas.restore();
              }

              if (pass.stroke) paintPasses(canvas, [{ ...pass, fill: null }], outlines, { letters: true });
              continue;
            }

            paintPasses(canvas, [pass], outlines, { letters: true });

            // The rules belong to the letters, and go down with them - after
            // anything an effect put behind. Same rectangles as the press.
            if (pass.body && placed.rules.length) {
              const ink = pass.fill && !isGradient(pass.fill) ? pass.fill : pass.stroke;

              if (ink) {
                canvas.fillStyle = toCss(ink);

                for (const rule of placed.rules) {
                  // Never thinner than a pixel on screen, or it vanishes at low zoom.
                  canvas.fillRect(rule.x, rule.y, rule.w, Math.max(rule.h, 1 / pxPerMm));
                }
              }
            }
          }

          canvas.restore();

          // Konva needs a hit region even though we drew straight to the context.
          context.beginPath();
          context.rect(0, 0, shape.width(), shape.height());
          context.closePath();
          context.fillStrokeShape(shape);
        }}
        hitFunc={(context, shape) => {
          // The whole box is clickable, not just the ink. Requiring a customer to
          // hit a letter stroke to select a word is a cruelty.
          context.beginPath();
          context.rect(0, 0, shape.width(), shape.height());
          context.closePath();
          context.fillStrokeShape(shape);
        }}
        width={width}
        height={height}
      />

      {editing && <Caret layer={layer} index={caret.end} zoom={zoom} />}
    </>
  );
}

/**
 * The blinking line.
 *
 * It stops blinking while the caret is moving, which is what every text field
 * does and what makes a caret findable during fast typing.
 */
function Caret({ layer, index, zoom }: { layer: TextLayer; index: number; zoom: number }) {
  const [on, setOn] = useState(true);

  useEffect(() => {
    setOn(true);

    const timer = window.setInterval(() => setOn((lit) => !lit), BLINK_MS);

    return () => window.clearInterval(timer);
  }, [index, layer.text]);

  const at = caretRect(layer, index);

  if (!at || !on) return null;

  return (
    <Rect
      x={mmToPx(at.x, zoom)}
      y={mmToPx(at.y, zoom)}
      width={1.5}
      height={mmToPx(at.height, zoom)}
      fill="#0b84ff"
      listening={false}
    />
  );
}
